import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@noor/ui';
import {
  MERCHANT_CHAT_STATUS,
  MERCHANT_THREAD_PAGE_SIZE,
  groupMerchantMessagesByDate,
  formatMerchantGroupLabel,
  merchantChatFileUrl,
  useMerchantThread,
  type MerchantChat,
} from '@/entities/merchant-chat';
import type { OpenMerchantChat } from './merchant-chat-drawer';
import { MerchantAttachmentLightbox } from './merchant-attachment-lightbox';
import { MerchantMessageBubble } from './merchant-message-bubble';
import { MerchantChatComposer } from './merchant-chat-composer';

/**
 * One thread — date-grouped bubbles newest-at-bottom (the wire arrives
 * newest-first; the container reads `column-reverse`, as the old window did),
 * growing upward by 15 as the merchant scrolls to the top.
 */
export function MerchantChatThread({
  chatId,
  chat,
  onCreated,
}: {
  chatId: Exclude<OpenMerchantChat, null>;
  chat: MerchantChat | null;
  onCreated: (id: number) => void;
}) {
  // Keyed per thread: switching threads resets the page, the scroll, and the
  // composer draft. (The old window kept the draft across switches, so text
  // typed in chat A could be sent into chat B — not reproduced on purpose.)
  return (
    <ThreadBody key={String(chatId)} chatId={chatId} chat={chat} onCreated={onCreated} />
  );
}

function ThreadBody({
  chatId,
  chat,
  onCreated,
}: {
  chatId: Exclude<OpenMerchantChat, null>;
  chat: MerchantChat | null;
  onCreated: (id: number) => void;
}) {
  const { i18n } = useTranslation();
  const numericId = typeof chatId === 'number' ? chatId : null;
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching } = useMerchantThread(numericId, page);
  // Memoized: the `?? []` fallback would otherwise hand every memo below a
  // fresh array identity on each render.
  const messages = useMemo(() => data?.data ?? [], [data]);
  const totalCount = data?.count ?? 0;

  // The old window's page math, verbatim: one page below 15 rows, else the
  // ceiling — more to load while the page lags behind it.
  const allPages =
    totalCount >= MERCHANT_THREAD_PAGE_SIZE
      ? Math.ceil(totalCount / MERCHANT_THREAD_PAGE_SIZE)
      : 1;
  const hasMore = allPages > page;

  const groups = groupMerchantMessagesByDate(messages);
  const isClosed =
    chat?.status === MERCHANT_CHAT_STATUS.closedResolved ||
    chat?.status === MERCHANT_CHAT_STATUS.closedUnresolved;

  // The viewer's image rail: every messaged attachment, oldest first.
  const threadImages = useMemo(
    () =>
      [...messages].reverse().flatMap((message) => {
        const url = merchantChatFileUrl(message.files?.[0]);
        return url ? [{ url, messageId: message.id }] : [];
      }),
    [messages],
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div
        className="flex min-h-0 flex-1 flex-col-reverse gap-1 overflow-y-auto p-3"
        onScroll={(event) => {
          // Fully scrolled up in a column-reverse box: negative scrollTop at
          // its extreme. Grow the cumulative slice while rows remain.
          const target = event.currentTarget;
          if (
            hasMore &&
            !isLoading &&
            !isFetching &&
            Math.abs(target.scrollTop) + target.clientHeight >= target.scrollHeight - 1
          ) {
            setPage((prev) => prev + 1);
          }
        }}
      >
        {isLoading && messages.length === 0 ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          groups.map(([groupKey, group]) => (
            <div key={groupKey} className="flex flex-col gap-1">
              {group.map((message) => (
                <MerchantMessageBubble
                  key={message.id}
                  message={message}
                  onImageOpen={(messageId) => {
                    const index = threadImages.findIndex(
                      (image) => image.messageId === messageId,
                    );
                    if (index !== -1) setLightboxIndex(index);
                  }}
                />
              ))}
              <div className="mb-1 flex items-center justify-center">
                <span className="rounded-lg bg-muted p-1 text-base font-medium text-foreground">
                  {formatMerchantGroupLabel(groupKey, i18n.language)}
                </span>
              </div>
            </div>
          ))
        )}
      </div>
      <div className="sticky bottom-0 z-10">
        {chatId === 'new' ? (
          <MerchantChatComposer mode="create" onCreated={onCreated} />
        ) : (
          !isClosed && <MerchantChatComposer mode="reply" chatId={chatId} />
        )}
      </div>
      {lightboxIndex !== null && (
        <MerchantAttachmentLightbox
          images={threadImages.map((image) => image.url)}
          index={lightboxIndex}
          onIndexChange={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}
